import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE = 'https://www.resinsurfaceslondon.co.uk';

/** Valid image URL for Organization.logo in JSON-LD (no /logo.png in public). */
const PUBLISHER_LOGO = `${SITE}/images/resin-surfaces-bound-work1.jpeg`;

export interface BlogArticleHelmetProps {
  /** Value inside <title>… | ZAN RESIN SURFACES LTD */
  pageTitle: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  /** Absolute image URL for OG / Twitter / JSON-LD */
  ogImageUrl: string;
  ogImageAlt: string;
  datePublished: string;
  dateModified?: string;
  articleSection: string;
  articleTags: string[];
  wordCount: number;
  timeRequiredMinutes: number;
  /** Optional extra "about" entities for BlogPosting */
  aboutThings?: string[];
}

function toIsoDate(d: string): string {
  if (d.includes('T')) return d.endsWith('Z') || d.includes('+') ? d : `${d}+00:00`;
  return `${d}T00:00:00+00:00`;
}

/**
 * Full article SEO: meta, Open Graph, Twitter, hreflang, geo, rich BlogPosting JSON-LD.
 * Use for long-form static blog pages so they match flagship posts and borough guides.
 */
const BlogArticleHelmet: React.FC<BlogArticleHelmetProps> = ({
  pageTitle,
  description,
  keywords,
  canonicalUrl,
  ogImageUrl,
  ogImageAlt,
  datePublished,
  dateModified = datePublished,
  articleSection,
  articleTags,
  wordCount,
  timeRequiredMinutes,
  aboutThings = ['Resin Bound Surfacing', 'Driveway Installation', 'London'],
}) => {
  const fullTitle = `${pageTitle} | ZAN RESIN SURFACES LTD`;
  const publishedIso = toIsoDate(datePublished);
  const modifiedIso = toIsoDate(dateModified);
  const ogDesc =
    description.length > 200 ? `${description.slice(0, 197).trim()}…` : description;

  const blogPosting = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: pageTitle,
    description,
    url: canonicalUrl,
    datePublished: publishedIso,
    dateModified: modifiedIso,
    author: {
      '@type': 'Organization',
      name: 'ZAN RESIN SURFACES LTD',
      url: SITE,
      logo: {
        '@type': 'ImageObject',
        url: PUBLISHER_LOGO,
      },
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'London',
        addressCountry: 'GB',
      },
    },
    publisher: {
      '@type': 'Organization',
      name: 'ZAN RESIN SURFACES LTD',
      url: SITE,
      logo: {
        '@type': 'ImageObject',
        url: PUBLISHER_LOGO,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl,
    },
    image: {
      '@type': 'ImageObject',
      url: ogImageUrl,
      width: 1200,
      height: 630,
    },
    wordCount,
    timeRequired: `PT${timeRequiredMinutes}M`,
    articleSection,
    keywords,
    inLanguage: 'en-GB',
    isAccessibleForFree: true,
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: 'h1, h2, h3, .excerpt',
    },
    about: aboutThings.map((name) => ({ '@type': 'Thing', name })),
  };

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="ZAN RESIN SURFACES LTD" />
      <meta name="publisher" content="ZAN RESIN SURFACES LTD" />
      <meta name="language" content="en-GB" />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={ogDesc} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={ogImageAlt} />
      <meta property="og:site_name" content="ZAN RESIN SURFACES LTD" />
      <meta property="og:locale" content="en_GB" />
      <meta property="article:published_time" content={publishedIso} />
      <meta property="article:modified_time" content={modifiedIso} />
      <meta property="article:author" content="ZAN RESIN SURFACES LTD" />
      <meta property="article:section" content={articleSection} />
      {articleTags.map((tag) => (
        <meta key={tag} property="article:tag" content={tag} />
      ))}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={ogDesc} />
      <meta name="twitter:image" content={ogImageUrl} />
      <meta name="twitter:image:alt" content={ogImageAlt} />
      <meta name="twitter:site" content="@resinsurfaceslondon" />
      <meta name="twitter:creator" content="@resinsurfaceslondon" />

      <meta name="geo.region" content="GB-LND" />
      <meta name="geo.placename" content="London" />
      <meta name="geo.position" content="51.5074;-0.1278" />
      <meta name="ICBM" content="51.5074, -0.1278" />

      <link rel="alternate" hrefLang="en-GB" href={canonicalUrl} />
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />

      <script type="application/ld+json">{JSON.stringify(blogPosting)}</script>
    </Helmet>
  );
};

export default BlogArticleHelmet;
